import React from 'react';
import { ExternalLink, Github, Star } from 'lucide-react';

const Projects = () => {
  const projects = [
    {
      title: "SamWise: Learning Companion for Justice-Impacted Students",
      description: "A conversation-based study tool enabling justice-impacted students to demonstrate proficiency through conversation. Based on two years of teaching entrepreneurshp in Boston-area jails and prisons.",
      technologies: ["React", "JavaScript", "Tailwind CSS", "LLMs"],
      liveUrl: "https://gosamwise.org",
      githubUrl: null,
      featured: true
    },
    {
      title: "Front Row Fantasy",
      description: "A social music game for friends to discover and share new music - like fantasy sports, but for music fans.",
      technologies: ["Convex", "Typescript", "React", "LLMs"],
      liveUrl: "https://www.frontrowvibes.com",
      githubUrl: null,
      featured: false
    },
    {
      title: "Hands On Deep Learning: Preventing Eviction with RAG",
      description: "Worked with Memphis-based tenants-rights organization to build a tool helping tenants react to eviction notices.",
      technologies: ["Python", "NLP", "Embedded-based retrieval"],
      liveUrl: null,
      githubUrl: "https://colab.research.google.com/drive/1RDmxgEDgHlxKU6qrOM7iK4Rx1ktoViV4?usp=sharing",
      featured: false
    },
    {
      title: "Data Classroom",
      description: "An datascience education platform for secondary and post-secondary classrooms. I wrote lessons, prototyped features based on customer feedback sessions I led, and supported the two co-founders on any task the team needed. Great learning experience and a lot of fun working with educators.",
      technologies: ["Product Management", "Customer Success", "Strategy and Ops"],
      liveUrl: "https://dataclassroom.com",
      githubUrl: null,
      featured: false
    },
    {
      title: "Our World",
      description: "I started a climate education website. Our goal was to become the Duolingo of science education. Noble, but very misguided. I've taken the website down, but here's a small example of its feel.",
      technologies: ["Figma", "Python"],
      liveUrl: null,
      githubUrl: null,
      featured: false,
      image: "/wourld_home.jpeg"
    }
  ];

  const ProjectCard = ({ project }) => {
    const accentColor = project.featured ? '#E53935' : '#1E88E5';
    return (
      <div className="mondrian-card p-6 h-full relative overflow-hidden">
        {project.featured && (
          <>
            <div className="absolute top-0 left-0 w-full h-2 bg-[#E53935]"></div>
            <div className="flex items-center gap-2 mb-4 mt-2">
              <Star className="w-5 h-5 text-[#FBC02D] fill-current" />
              <span className="text-black font-bold text-sm">Featured Project</span>
            </div>
          </>
        )}
        {project.image && (
          <div className="mb-4 -mx-6 -mt-6 overflow-hidden">
            <img 
              src={project.image} 
              alt={project.title}
              className="w-full h-48 object-cover border-b-4 border-black"
            />
          </div>
        )}
        <h3 className="text-xl font-bold mb-3 text-black">
          {project.title}
        </h3>
        <p className="text-gray-700 mb-4 leading-relaxed">
          {project.description}
        </p>
        <div className="flex flex-wrap gap-2 mb-6">
          {project.technologies.map((tech, index) => {
            const colors = ['#E53935', '#1E88E5', '#FBC02D'];
            const bgColor = colors[index % colors.length];
            return (
              <span 
                key={tech} 
                className="px-3 py-1 text-xs font-bold border-2 border-black text-white"
                style={{ backgroundColor: bgColor }}
              >
                {tech}
              </span>
            );
          })}
        </div>
        <div className="flex gap-3 mt-auto">
          {project.liveUrl && (
            <a
              href={project.liveUrl}
              className="flex items-center gap-2 bg-[#E53935] hover:bg-[#c62828] px-4 py-2 border-2 border-black transition-all duration-200 text-white font-bold"
            >
              <ExternalLink className="w-4 h-4" />
              Visit Website
            </a>
          )}
          {project.githubUrl && (
            <a
              href={project.githubUrl}
              className="flex items-center gap-2 bg-white hover:bg-gray-100 px-4 py-2 border-2 border-black transition-all duration-200 text-black font-bold"
            >
              <Github className="w-4 h-4" />
              Code
            </a>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-black">
            My Projects
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <ProjectCard key={index} project={project} />
          ))}
        </div>

        <div className="text-center mt-16">
          <div className="mondrian-card p-8 relative overflow-hidden">
            <div className="absolute top-0 left-0 h-full accent-bar-yellow"></div>
            <h2 className="text-2xl font-bold text-black mb-4">Want to collaborate?</h2>
            <p className="text-gray-700 mb-6">
              I'm always excited to work on new projects and connect with fellow builders.
            </p>
            <a
              href="mailto:beblaze@mit.edu"
              className="inline-flex items-center gap-2 bg-[#1E88E5] hover:bg-[#1565c0] px-6 py-3 border-2 border-black transition-all duration-200 text-white font-bold"
            >
              Get in Touch
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Projects;